// 外見動画の変換（ブラウザ内の x264）。動画を GOP ごとの区間に分けて Worker で並列にエンコードし、最後に連結する
// 区間の分け方は PC のコア数に依らないので、どの PC でも同じ出力になる（コア数は速さだけに効く）
/* global globalThis */
import { planEncode, buildChunkArgs, buildConcatArgs, buildConcatList, calcEncodeWorkers } from './encodeSpec';
import { optimizedFileName } from './videoOptimizeSpec';
import { loadEncoderAssets } from './encoderAssets';
import { readVideoMeta } from './videoMeta';
import { createWorkerPool } from './workerPool';
import { createEncodeWorker } from './encodeWorkerFactory';
import { OptimizeError } from './videoOptimize';

const defaultCreatePool = ({ coreURL, wasmURL }, size) => createWorkerPool({
    size,
    createWorker: createEncodeWorker,
    initMessage: { type: 'init', coreURL, wasmURL },
});

// WORKERFS は File の名前でマウントされるので、ffmpeg の引数に使える名前に付け替える（中身はコピーされない）
const sourceOf = (file) => {
    const dot = file.name.lastIndexOf('.');
    const ext = dot > 0 ? file.name.slice(dot).toLowerCase().replace(/[^.a-z0-9]/g, '') : '';
    return new File([file], `source${ext}`, { type: file.type });
};

const toOptimizeError = (e) => {
    if (e instanceof OptimizeError) return e;
    if (e && e.code === 'cancelled') return new OptimizeError('cancelled');
    return new OptimizeError('failed', [e && e.message, e && e.log].filter(Boolean).join('\n'));
};

/**
 * モーダル1つにつき1つ作り、閉じるときに dispose する（Worker の集まりを全種別で共有する）
 * @returns {{ encode: (file: File, slotKey: string, callbacks?: { onStart?: Function, onProgress?: Function }) => { promise: Promise<{ file: File, info: object }>, cancel: () => void }, dispose: () => Promise<void> }}
 */
export const createVideoEncoder = ({
    readMeta = readVideoMeta,
    loadAssets = loadEncoderAssets,
    createPool = defaultCreatePool,
    cores = globalThis.navigator && globalThis.navigator.hardwareConcurrency,
} = {}) => {
    let poolPromise = null;
    let disposed = false;
    let mountSeq = 0;

    const getPool = () => {
        if (!poolPromise) {
            poolPromise = (async () => {
                const assets = await loadAssets();
                return createPool(assets, calcEncodeWorkers(cores));
            })();
            // 失敗したら次の変換で取り直す
            poolPromise.catch(() => { poolPromise = null; });
        }
        return poolPromise;
    };

    const encode = (file, slotKey, { onStart, onProgress } = {}) => {
        const owner = Symbol('encode');
        let pool = null;
        let cancelled = false;
        let rejectCancel = null;

        const work = (async () => {
            const meta = await readMeta(file);
            const plan = planEncode(meta, slotKey);
            if (plan.frames === 0) throw new OptimizeError('no_video');
            try {
                pool = await getPool();
            } catch (e) {
                throw e instanceof OptimizeError ? e : new OptimizeError('encoder_unavailable', e && e.message);
            }
            if (cancelled) throw new OptimizeError('cancelled');

            const source = sourceOf(file);
            const mount = { key: `m${++mountSeq}`, file: source };
            const done = plan.chunks.map(() => 0);
            let started = false;
            const report = () => {
                if (onProgress) onProgress(Math.min(0.99, done.reduce((a, b) => a + b, 0) / plan.frames));
            };

            const outputs = await Promise.all(plan.chunks.map((chunk) => {
                const output = `c${chunk.index}.mp4`;
                return pool.run(
                    { type: 'run', mount, args: buildChunkArgs(plan, chunk, `/in/${source.name}`, output), output },
                    {
                        owner,
                        onStart: () => {
                            if (started) return;
                            started = true;
                            if (onStart) onStart();
                        },
                        onProgress: ({ frame }) => {
                            done[chunk.index] = Math.min(chunk.frames, frame);
                            report();
                        },
                    },
                ).then(({ data }) => {
                    done[chunk.index] = chunk.frames;
                    report();
                    return data;
                });
            })).catch((e) => {
                // 1区間でも失敗したら、残りの区間は無駄になるので止める
                pool.cancel(owner);
                throw e;
            });

            const names = outputs.map((_, i) => `c${i}.mp4`);
            const files = [
                ...outputs.map((data, i) => ({ path: names[i], data })),
                { path: 'list.txt', data: Uint8Array.from(buildConcatList(names), (c) => c.charCodeAt(0)).buffer },
            ];
            const { data } = await pool.run(
                { type: 'run', files, args: buildConcatArgs('list.txt', 'out.mp4'), output: 'out.mp4' },
                // 連結はすぐ終わるので、ほかの動画の区間より先に回して完了を待たせない
                { owner, transfer: files.map((f) => f.data), priority: true },
            );
            if (onProgress) onProgress(1);
            return {
                file: new File([data], optimizedFileName(file.name), { type: 'video/mp4' }),
                info: { width: plan.target.width, height: plan.target.height, fps: plan.fps, frames: plan.frames, resized: plan.resized },
            };
        })();

        const cancelPromise = new Promise((_, reject) => { rejectCancel = reject; });
        const promise = Promise.race([work, cancelPromise]).catch((e) => { throw toOptimizeError(e); });
        // 取消で work 側が後から失敗しても未処理の reject にしない
        work.catch(() => {});

        const cancel = () => {
            if (cancelled) return;
            cancelled = true;
            if (pool) pool.cancel(owner);
            rejectCancel(new OptimizeError('cancelled'));
        };
        return { promise, cancel };
    };

    const dispose = async () => {
        if (disposed) return;
        disposed = true;
        if (!poolPromise) return;
        try {
            (await poolPromise).dispose();
        } catch (e) {
            // 起動に失敗した pool は破棄するものが無い
        }
    };

    return { encode, dispose };
};
